import { Link } from 'react-router-dom';
import { categoryQuickLinks } from '@/mocks/categories';
import { useState, useRef, useEffect } from 'react';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20fashion%20accessories%20flatlay%20with%20colorful%20scarves%20sunglasses%20watches%20and%20delicate%20jewelry%20on%20soft%20cream%20linen%20background%20natural%20morning%20light%20elegant%20Scandinavian%20composition%20editorial%20photography%20warm%20neutral%20tones%20sophisticated%20arrangement%20soft%20shadows%20clean%20aesthetic&width=1600&height=900&seq=hero-bg-v3-new&orientation=landscape"
          alt="Hipobuy Spreadsheet - Gestion de données intelligente"
          title="Hipobuy Spreadsheet - Solution moderne de gestion de données"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 flex flex-col items-center text-center">
        {/* Top Badge */}
        <div className="animate-fade-in-up mb-8">
          <span className="inline-flex items-center gap-2 bg-background-50/90 backdrop-blur-sm text-foreground-800 px-4 py-2 rounded-full text-sm font-medium border border-background-200/50">
            <span className="w-2 h-2 rounded-full bg-accent-500"></span>
            Nouvelle Génération de Spreadsheet – 2026
          </span>
        </div>

        {/* Main Title */}
        <h1 className="animate-fade-in-up animate-delay-100 text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-background-50 leading-[1.05] tracking-tight mb-6 drop-shadow-lg">
          Hipobuy
          <br />
          <span className="text-background-50/90">Spreadsheet</span>
        </h1>

        {/* Subtitle */}
        <p className="animate-fade-in-up animate-delay-200 text-lg md:text-xl text-background-50/90 max-w-[600px] mb-10 leading-relaxed font-light">
          L&apos;alternative moderne aux tableurs traditionnels. Gérez votre catalogue
          produits avec une élégance minimale et une puissance analytique inégalée.
        </p>

        {/* Hero CTAs */}
        <div className="animate-fade-in-up animate-delay-300 flex flex-col sm:flex-row items-center gap-4 mb-14">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="btn-cta inline-flex items-center gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-4 rounded-full text-base font-semibold shadow-lg shadow-primary-500/25 transition-all whitespace-nowrap cursor-pointer"
          >
            hipobuy spreadsheet
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-up-line"></i>
            </span>
          </a>

          {/* Category Dropdown in Hero */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="btn-cta inline-flex items-center gap-3 bg-background-50/20 backdrop-blur-sm hover:bg-background-50/30 text-background-50 px-8 py-4 rounded-full text-base font-medium border border-background-50/30 transition-all whitespace-nowrap cursor-pointer"
            >
              Catégories
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[720px] bg-background-50 border border-background-200/70 rounded-xl shadow-xl py-4 px-5 z-50 text-left">
                <p className="px-3 py-1 text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Toutes les catégories</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                  {categoryQuickLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-primary-100/70 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Link to shopping guide */}
        <div className="animate-fade-in-up animate-delay-350 mb-14">
          <Link
            to="/guide-achat"
            className="inline-flex items-center gap-2 text-sm text-background-50/75 hover:text-background-50 transition-colors border border-background-50/25 rounded-full px-5 py-2.5 hover:border-background-50/40"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line"></i>
            </span>
            Guide d&apos;achat
          </Link>
        </div>

        {/* Stats mini */}
        <div className="animate-fade-in-up animate-delay-400 flex flex-wrap items-center justify-center gap-8 md:gap-12 text-background-50/80">
          <div className="text-center">
            <p className="text-3xl md:text-4xl font-heading font-bold drop-shadow">15+</p>
            <p className="text-xs mt-1 text-background-50/60">Catégories</p>
          </div>
          <div className="w-px h-10 bg-background-50/20 hidden sm:block"></div>
          <div className="text-center">
            <p className="text-3xl md:text-4xl font-heading font-bold drop-shadow">10K+</p>
            <p className="text-xs mt-1 text-background-50/60">Utilisateurs</p>
          </div>
          <div className="w-px h-10 bg-background-50/20 hidden sm:block"></div>
          <div className="text-center">
            <p className="text-3xl md:text-4xl font-heading font-bold drop-shadow">99%</p>
            <p className="text-xs mt-1 text-background-50/60">Uptime</p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 rounded-full border-2 border-background-50/30 flex items-start justify-center pt-2">
            <div className="w-1 h-2.5 rounded-full bg-background-50/50 animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
}